/**
 * 見出しのドラッグで列を落とす先を、ポインタの位置と見出しセルの画面上の位置から決める（#3690 VOC_PJ-577 案 1-B）。
 *
 * 横スクロール中は、固定列の下にスクロール側の列が潜り込んでいる。dnd-kit 標準の当たり判定
 * （closestCenter など）は見えていない列も候補にするので、ここで次のように分ける。
 * - ポインタが固定の境界（固定列の右端）より左: 固定列だけを候補にし、落とすと固定する
 * - 境界より右: スクロール側の列の「境界より右に見えている部分」だけを候補にし、落とすと固定を外す
 */

export type HeaderCellRect = {
  key: string
  left: number
  right: number
  isFixed: boolean
}

export type HeaderDropTarget = {
  /** 落としたあとに固定するか */
  isFixed: boolean
  /** この列の直前に置く。null は範囲の末尾 */
  beforeKey: string | null
  /** 落とし先を示す縦線の x 座標（見出しセルと同じ座標系） */
  lineX: number
}

export const resolveHeaderDropTarget = (
  rects: HeaderCellRect[],
  pointerX: number,
  draggingKey: string,
  /**
   * 先頭の固定必須列の key。この列の前には落とせないので、前に落とそうとしたら直後を落とし先にする
   * （7-A: 固定必須列は左端から押し出されない）
   */
  { firstLockedKey }: { firstLockedKey?: string } = {}
): HeaderDropTarget | null => {
  const fixedRects = rects.filter(({ isFixed }) => isFixed)
  if (fixedRects.length === 0) return null
  const boundary = Math.max(...fixedRects.map(({ right }) => right))
  const isFixed = pointerX < boundary

  const candidates = (
    isFixed
      ? fixedRects
      : rects
          .filter((rect) => !rect.isFixed && rect.right > boundary)
          .map((rect) => ({ ...rect, left: Math.max(rect.left, boundary) }))
  ).filter(({ key }) => key !== draggingKey)

  const beforeIndex = candidates.findIndex(
    ({ left, right }) => (left + right) / 2 > pointerX
  )
  const isBeforeLocked =
    isFixed && beforeIndex === 0 && candidates[0].key === firstLockedKey
  const before = candidates[isBeforeLocked ? 1 : beforeIndex]
  if (beforeIndex >= 0 && before) {
    return { isFixed, beforeKey: before.key, lineX: before.left }
  }
  if (isBeforeLocked) {
    return { isFixed, beforeKey: null, lineX: candidates[0].right }
  }

  const last = candidates.at(-1)
  return {
    isFixed,
    beforeKey: null,
    lineX: last ? last.right : boundary
  }
}
